import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Autocomplete,
  Fab,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  Box,
  Dialog as ConfirmDialog,
  DialogTitle as ConfirmDialogTitle,
  DialogActions as ConfirmDialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { API_BASE_URL } from '../../../constants/constants';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../context/PermissionContext';
import BackButton from '../../../components/BackButton';

const DeletePincode = () => {
  const [pincodes, setPincodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTaluk, setSelectedTaluk] = useState(null);
  const [selectedPincodes, setSelectedPincodes] = useState([]);
  const [removedPincodes, setRemovedPincodes] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [message, setMessage] = useState('');
  const { can } = usePermissions();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDistricts();
    fetchPincodes();
  }, []);

  const fetchPincodes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/pincodes/details`);
      if (Array.isArray(response.data)) {
        setPincodes(response.data);
      } else {
        setPincodes([]);
      }
    } catch (error) {
      console.error('Error fetching pincodes:', error);
      setPincodes([]);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/districts`);
      setDistricts(response.data);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchTaluks = async (districtId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/taluks/district/${districtId}`);
      setTaluks(response.data);
    } catch (error) {
      console.error('Error fetching taluks:', error);
    }
  };

  const fetchTalukPincodes = async (talukId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/pincodes/taluk/${talukId}`);
      if (response.data.pincodes.length > 0) {
        setSelectedPincodes(response.data.pincodes);
      } else {
        setMessage('No pincodes found for this constituency.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching taluk pincodes:', error);
    }
  };

  const handleDistrictChange = (event, value) => {
    setSelectedDistrict(value);
    setSelectedTaluk(null);
    setSelectedPincodes([]);
    setRemovedPincodes([]);
    if (value) {
      fetchTaluks(value._id);
    }
  };

  const handleTalukChange = (event, value) => {
    setSelectedTaluk(value);
    setSelectedPincodes([]);
    setRemovedPincodes([]);
    if (value) {
      fetchTalukPincodes(value._id);
    }
  };

  const handlePincodeDelete = (pincode) => {
    setRemovedPincodes([...removedPincodes, pincode]);
    setSelectedPincodes(selectedPincodes.filter(pc => pc !== pincode));
  };

  const handleOpenDialog = () => {
    if (!can('master.pincodeRemove.delete')) return;
    setOpen(true);
  };

  const handleCloseDialog = () => {
    if (removedPincodes.length > 0) {
      setConfirmDialogOpen(true);
    } else {
      clearDialogData();
    }
  };

  const clearDialogData = () => {
    setOpen(false);
    setSelectedDistrict(null);
    setSelectedTaluk(null);
    setSelectedPincodes([]);
    setRemovedPincodes([]);
  };

  const handleSubmit = async () => {
    if (!can('master.pincodeRemove.delete')) return;
    if (selectedDistrict && selectedTaluk && removedPincodes.length > 0) {
      try {
        await axios.delete(`${API_BASE_URL}/pincodes`, {
          data: {
            districtId: selectedDistrict._id,
            talukId: selectedTaluk._id,
            pincodes: removedPincodes,
          },
        });

        setSnackbarSeverity('success');
        setMessage('Pincode(s) deleted successfully.');
        setSnackbarOpen(true);
        clearDialogData();
        fetchPincodes();
      } catch (error) {
        console.error('Error deleting pincodes:', error);
        setSnackbarSeverity('error');
        setMessage(error.response.data.message || 'Error deleting pincodes');
        setSnackbarOpen(true);
      }
    } else {
      setSnackbarSeverity('error');
      setMessage('Please select pincodes to delete.');
      setSnackbarOpen(true);
    }
  };

  const handleExportCSV = () => {
    if (!can('master.pincodeRemove.export')) return;
    if (!pincodes || pincodes.length === 0) return;
    const headers = ['SL', 'District', 'Constituency', 'Pincodes'];
    const csvRows = [headers.join(',')];

    pincodes.forEach((p, idx) => {
      const sl = idx + 1;
      const dName = p.districtName || 'N/A';
      const cName = p.talukName || 'N/A';
      const pins = Array.isArray(p.pincodes) ? p.pincodes.join(';') : '';

      csvRows.push([sl, `"${dName}"`, `"${cName}"`, `"${pins}"`].join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `pincodes_report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmDialogProceed = () => {
    setConfirmDialogOpen(false);
    clearDialogData();
  };

  const columns = [
    {
      field: 'sl',
      headerName: 'SL',
      width: 70,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
          {params.row.sl}
        </Typography>
      ),
    },
    { field: 'districtName', headerName: 'District', width: 190 },
    { field: 'talukName', headerName: 'Constituency', width: 180 },
    { field: 'pincodes', headerName: 'Pincodes', width: 350, renderCell: (params) => (params.value || []).join(', ') },
  ];

  const rows = pincodes.map((pincode, index) => ({
    id: index + 1,
    sl: index + 1,
    districtName: pincode.districtName,
    talukName: pincode.talukName,
    pincodes: pincode.pincodes,
  }));

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br /><br />
      <Box sx={{ height: 400, width: '100%', position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Pincode Management
          </Typography>
          {can('master.pincodeRemove.export') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              sx={{ fontWeight: 700, mr: 7 }}
            >
              Export CSV
            </Button>
          )}
        </Box>
        <DataGrid rows={rows} columns={columns} autoHeight getRowHeight={() => 'auto'} />
        {can('master.pincodeRemove.delete') && (
          <Tooltip title="Delete Pincodes">
            <Fab
              color="secondary"
              aria-label="delete"
              sx={{ position: 'absolute', top: -7, right: 16 }}
              onClick={handleOpenDialog}
            >
              <DeleteIcon />
            </Fab>
          </Tooltip>
        )}
        <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle>Delete Pincodes</DialogTitle>
          <DialogContent>
            <Autocomplete
              options={districts}
              getOptionLabel={(option) => (option && option.name) ? option.name : ''}
              isOptionEqualToValue={(option, value) => (option && value ? option._id === value._id : false)}
              filterOptions={(options, state) =>
                options.filter(option => (option && option.name) ? option.name.toLowerCase().includes((state.inputValue || '').toLowerCase()) : false)
              }
              value={selectedDistrict}
              onChange={handleDistrictChange}
              renderInput={(params) => <TextField {...params} label="Select District" placeholder="Search or select district..." variant="outlined" />}
              sx={{ mb: 2, mt: 1 }}
            />
            <Autocomplete
              options={taluks}
              getOptionLabel={(option) => (option && option.name) ? option.name : ''}
              isOptionEqualToValue={(option, value) => (option && value ? option._id === value._id : false)}
              filterOptions={(options, state) =>
                options.filter(option => (option && option.name) ? option.name.toLowerCase().includes((state.inputValue || '').toLowerCase()) : false)
              }
              value={selectedTaluk}
              onChange={handleTalukChange}
              renderInput={(params) => <TextField {...params} label="Select Constituency" placeholder="Search or select constituency..." variant="outlined" />}
              sx={{ mb: 2 }}
            />
            {selectedPincodes.length > 0 ? (
              <Box sx={{ mb: 2 }}>
                {selectedPincodes.map((pincode, index) => (
                  <Chip
                    key={index}
                    label={pincode}
                    onDelete={() => handlePincodeDelete(pincode)}
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            ) : (
              <Typography>No pincodes found for this Constituency.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => navigate('/pincode/create')}
              color="primary"
              variant="contained"
              sx={{ mr: 'auto' }}
            >
              Create Pincode
            </Button>
            <Button onClick={handleCloseDialog} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              Submit
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ whiteSpace: 'pre-line' }}>
            {message}
          </Alert>
        </Snackbar>

        <ConfirmDialog
          open={confirmDialogOpen}
          onClose={handleConfirmDialogClose}
        >
          <ConfirmDialogTitle>Unsaved Changes</ConfirmDialogTitle>
          <DialogContent>
            <Typography>You have unsaved changes. Do you really want to close?</Typography>
          </DialogContent>
          <ConfirmDialogActions>
            <Button onClick={handleConfirmDialogClose} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDialogProceed} color="primary" variant="contained">
              Proceed
            </Button>
          </ConfirmDialogActions>
        </ConfirmDialog>
      </Box>
    </Box>
  );
};

export default DeletePincode;
